/**
 * What a machine's readiness report (Machine.readiness, R-ON-4, ADR 0090 decision 4) says, in
 * words: the drawer's rows, the one line Add machine and Getting started show, the sentence
 * where a person must act, and which found repositories can be added as projects.
 *
 * A report names repositories relative to the home folder (never an absolute path, so it
 * cannot leak one). To add one, the console finds the home folder from a path it already
 * knows on that machine (a checkout or a suggestion that ends with a reported path) and joins
 * the rest onto it; a repository it cannot place is listed without the action.
 */
import { agentKindLabel } from '@orgus/protocol';
import type { AgentKind, Checkout, HarnessReadiness, Machine, MachineReadiness, ProjectSuggestion } from '@orgus/protocol';
import { formatInt, plural } from '@orgus/ui';

/** "2.1" from "2.1.3 (Claude Code)", "2.47" from "2.47.1.windows.1"; null when there is none. */
export function shortVersion(v: string | null | undefined): string | null {
  const m = /(\d+)\.(\d+)/.exec(v ?? '');
  return m ? `${m[1]}.${m[2]}` : null;
}

/** How a row reads its state: done (quiet), act (a person must do something), off (absent), unknown. */
export type ReadinessTone = 'ok' | 'act' | 'off' | 'unknown';

export interface ReadinessRow {
  key: string;
  /** The harness, for its icon; absent for git and repositories. */
  kind?: AgentKind;
  title: string;
  /** One meta fact (a version), or null. */
  meta: string | null;
  tone: ReadinessTone;
  status: string;
}

function harnessRow(h: HarnessReadiness): ReadinessRow {
  const base = { key: `h:${h.kind}`, kind: h.kind, title: agentKindLabel(h.kind), meta: h.installed ? shortVersion(h.version) : null };
  if (!h.installed) return { ...base, tone: 'off', status: 'Not installed' };
  if (h.signIn === 'signed-in') return { ...base, tone: 'ok', status: 'Signed in' };
  if (h.signIn === 'signed-out') return { ...base, tone: 'act', status: 'Signed out' };
  return { ...base, tone: 'unknown', status: 'Sign-in unknown' };
}

/** The drawer's rows: each harness, git, then the repositories found. */
export function readinessRows(r: MachineReadiness, watched: number): ReadinessRow[] {
  const git: ReadinessRow = !r.git.installed
    ? { key: 'git', title: 'Git', meta: null, tone: 'off', status: 'Not installed' }
    : r.git.identityConfigured
      ? { key: 'git', title: 'Git', meta: shortVersion(r.git.version), tone: 'ok', status: 'Identity set' }
      : { key: 'git', title: 'Git', meta: shortVersion(r.git.version), tone: 'act', status: 'No identity' };
  const repos: ReadinessRow =
    r.repos.count > 0
      ? { key: 'repos', title: `${plural(r.repos.count, 'repository', 'repositories')} found`, meta: null, tone: 'ok', status: watched ? `${formatInt(watched)} watched` : 'None watched yet' }
      : { key: 'repos', title: 'No repositories found', meta: null, tone: 'off', status: 'None yet' };
  return [...r.harnesses.map(harnessRow), git, repos];
}

function joinWords(list: string[]): string {
  if (list.length <= 1) return list.join('');
  return `${list.slice(0, -1).join(', ')} and ${list[list.length - 1]}`;
}

/**
 * The one line: "Claude Code (signed in), git, 3 repositories". Only what was found: a harness
 * that is not installed is left out, as is git when it is missing.
 */
export function foundSummary(r: MachineReadiness): string {
  const parts: string[] = [];
  for (const h of r.harnesses) {
    if (!h.installed) continue;
    const state = h.signIn === 'signed-in' ? 'signed in' : h.signIn === 'signed-out' ? 'signed out' : null;
    parts.push(state ? `${agentKindLabel(h.kind)} (${state})` : agentKindLabel(h.kind));
  }
  if (r.git.installed) parts.push('git');
  if (r.repos.count > 0) parts.push(plural(r.repos.count, 'repository', 'repositories'));
  return parts.length ? parts.join(', ') : 'no agent, git or repository yet';
}

/** "Found on this computer: ..." or "Found on studio: ...". */
export function foundLine(r: MachineReadiness, where: string): string {
  return `Found on ${where}: ${foundSummary(r)}`;
}

/**
 * Where a person must act on that machine, one sentence each: harnesses signed out, git
 * without an identity. Empty when nothing needs them.
 */
export function readinessAsks(r: MachineReadiness, machineName: string): string[] {
  const out: string[] = [];
  const signedOut = r.harnesses.filter((h) => h.installed && h.signIn === 'signed-out').map((h) => agentKindLabel(h.kind));
  if (signedOut.length) {
    const many = signedOut.length > 1;
    out.push(`${joinWords(signedOut)} ${many ? 'are' : 'is'} signed out on ${machineName}: sign in there before Orgus runs ${many ? 'their' : 'its'} sessions.`);
  }
  if (r.git.installed && !r.git.identityConfigured) out.push(`Git on ${machineName} has no identity: set user.name and user.email there so commits carry an author.`);
  return out;
}

export interface FoundRepo {
  /** As reported: relative to the home folder, forward slashes. */
  rel: string;
  name: string;
  /** A checkout on this machine is already in it. */
  watched: boolean;
  /** The absolute path to add it by, when the home folder is known; null otherwise. */
  path: string | null;
}

const norm = (p: string) => p.replace(/\\/g, '/').replace(/\/+$/, '');

/** True when `abs` names `rel` under some folder (case-insensitive on Windows paths). */
function endsWithRel(abs: string, rel: string, fold: boolean): string | null {
  const a = norm(abs);
  const x = fold ? a.toLowerCase() : a;
  const r = fold ? rel.toLowerCase() : rel;
  if (!x.endsWith(`/${r}`)) return null;
  return a.slice(0, a.length - rel.length - 1);
}

/**
 * The repositories a report found on machine `m`, with whether each is watched and the path to
 * add it by. The home folder is learnt from any checkout or suggestion on that machine whose
 * path ends with a reported one.
 */
export function foundRepos(m: Pick<Machine, 'id' | 'os'>, r: MachineReadiness, checkouts: Checkout[], suggestions: ProjectSuggestion[]): FoundRepo[] {
  const fold = m.os === 'windows' || m.os === 'macos';
  const known = [...checkouts.filter((c) => c.machineId === m.id).map((c) => c.path), ...suggestions.filter((s) => s.machineId === m.id).map((s) => s.path)];
  const watchedPaths = checkouts.filter((c) => c.machineId === m.id).map((c) => c.path);
  let home: string | null = null;
  // The most specific reported path first: a one-folder path could match a folder elsewhere.
  const bySpecificity = [...r.repos.paths].sort((a, b) => b.split('/').length - a.split('/').length);
  for (const rel of bySpecificity) {
    for (const p of known) {
      home = endsWithRel(p, rel, fold);
      if (home) break;
    }
    if (home) break;
  }
  return r.repos.paths.map((rel) => ({
    rel,
    name: rel.split('/').pop() || rel,
    watched: watchedPaths.some((p) => endsWithRel(p, rel, fold) !== null),
    path: home ? `${home}/${rel}` : null,
  }));
}
